import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Typography } from "../Typography";
import { isValidColorKey } from "../../../theme";
import { Flex } from "../../layouts/wrappers";


interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "warning" | "outline" | "ghost" | "text" | "flat" | "link";
  size?: "small" | "medium" | "large" | "compact" | "fit"; // ✅ Add 'fit'
  tight?: boolean; // ✅ Optional flag to reduce padding even more
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  autoDisable?: boolean;
  rounded?: boolean;
  success?: boolean;  // ✅ Ensure success prop
  error?: boolean;  // ✅ Ensure error prop
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  iconOnly?: boolean;
  outlined?: boolean;
  ariaLabel?: string;

}

/** 🔄 Loading Spinner Animation */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/** ✅ Styled Button with Improved Sizing & Responsiveness */
const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;
  text-align: center;
  font-weight: ${({ theme }) => theme.fonts.fontWeightMedium};
  border-radius: ${({ theme, rounded }) => (rounded ? theme.radii.rounded : theme.radii.md)};
  

  max-width: ${({ fullWidth }) => (fullWidth ? "100%" : "280px")}; /* ✅ New max width */
  min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "80px")}; /* ✅ Prevent too-small buttons */

  /* ✅ Proper Button Sizing */
  ${({ size, theme, tight }) => {
    if (size === "fit") {
      return `
        font-size: 14px;
        padding: 0;
        min-height: auto;
        min-width: auto;
        line-height: 1;
      `;
    }

    const sizeStyles = {
      small: `
        font-size: 14px;
        padding: ${theme.spacing.xs} ${tight ? theme.spacing.xs : theme.spacing.sm};
        min-height: 36px;
      `,
      medium: `
        font-size: 16px;
        padding: ${theme.spacing.sm} ${tight ? theme.spacing.sm : theme.spacing.lg};
        min-height: 44px;
      `,
      large: `
        font-size: 18px;
        padding: ${theme.spacing.md} ${tight ? theme.spacing.md : theme.spacing.xxl};
        min-height: 52px;
      `,
      compact: `
        font-size: 14px;
        padding: ${theme.spacing.xs} ${tight ? theme.spacing.xs : theme.spacing.sm};
        min-height: 32px;
        min-width: 60px;
      `,
      default: `
        font-size: 16px;
        padding: ${theme.spacing.sm} ${tight ? theme.spacing.sm : theme.spacing.md};
        min-height: 44px;
      `,
    };

    return sizeStyles[size || "default"];
  }}

  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};

  /* ✅ Button Colors & Styles */
  background: ${({ success, error, variant, theme, disabled , outlined}) => {
    if (disabled) return theme.colors.buttonDisabled;
    if (success) return theme.colors.success;  // ✅ Apply success color
    if (error) return theme.colors.error;  // ✅ Apply error color
    if (variant === "flat") return 'transparent'; // ✅ Ensure readable text

    if (outlined ) return "transparent";

    return variant && isValidColorKey(variant, theme.colors)
        ? theme.colors[variant]
        : theme.colors.primary;

    // return variant === "secondary" ? theme.colors.secondary : theme.colors.primary;
  }};

  color: ${({ success, error, variant, theme, disabled, outlined }) => {
    if (disabled) return theme.colors.buttonDisabledText;
    if (success || error) return theme.colors.buttonText; // ✅ Ensure readable text
    if (variant === "flat") return theme.colors.primary; // ✅ Ensure readable text
    
    
    if (outlined) {
      return variant && isValidColorKey(variant, theme.colors)
        ? theme.colors[variant]
        : theme.colors.secondary;
    }
    if (variant === "primary")
      return theme.colors.textonPrimary;
    
    if (variant === "secondary")
      return theme.colors.textonSecondary;

    return  theme.colors.buttonText;
  }};

  border: ${({ variant, theme, outlined }) =>
  {
    if (variant === "flat") return 'none'; // ✅ Ensure readable text
    if (outlined) {
      return variant === "primary" ? `.5px solid ${theme.colors.primary}` : `.5px solid ${theme.colors.secondary}`
    }
    return  "none"
  }
  };

  box-shadow: ${({ theme, variant, disabled }) => {
    if (variant === "flat") return 'none'; // ✅ Ensure readable text
    return (disabled ? "none" : theme.elevations.low)
  }};

  &:hover {
    background: ${({ success, error, variant, theme, disabled , outlined}) =>
      disabled
        ? theme.colors.buttonDisabled
        : success
        ? theme.colors.success
        : error
        ? theme.colors.error
        : variant === "flat" 
        ? 'transparent'
        : variant === "secondary"
        ? theme.colors.secondaryHover
        : theme.colors.primaryHover};

    color: ${({ success, error, variant, theme, disabled,outlined }) =>{
      if (disabled) return theme.colors.buttonDisabledText;
      if (success || error) return theme.colors.buttonText; // ✅ Ensure readable text
      if (variant === "flat") return theme.colors.primary; // ✅ Ensure readable text
      // return variant === "outline" ? 'transparent' : 'transparent';
      if (outlined) return variant === "primary" ? theme.colors.textonPrimary : theme.colors.textonSecondary;
      return  theme.colors.buttonText;

    }};

    box-shadow: ${({ theme }) => theme.elevations.md};
  }

  &:active {
    background: ${({ success, error, variant, theme, disabled }) =>
      disabled
        ? theme.colors.buttonDisabled
        : success
        ? theme.colors.success
        : error
        ? theme.colors.error
        : variant === "secondary"
        ? theme.colors.secondarySelected
        : theme.colors.primarySelected};
    box-shadow: ${({ theme }) => theme.elevations.high};
  }

  /* ✅ Icon-Only Button Optimizations */
  ${({ iconOnly, size, theme }) =>
    iconOnly &&
    size !== "fit" &&
    `
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: ${
        size === "small" ? "36px" : size === "large" ? "52px" : "44px"
      };
      height: ${
        size === "small" ? "36px" : size === "large" ? "52px" : "44px"
      };
      padding: 0;
      color: ${theme.colors.buttonText};
      min-width: 0;
      min-height: 0;
    `}

  /* 🔄 Loading Spinner */
  .spinner {
    position: absolute;
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ${spin} 0.6s linear infinite;
  }

  /* ✅ Button Content Handling */
  .button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    visibility: ${({ isLoading }) => (isLoading ? "hidden" : "visible")};
  }
`;
const StyledTypographyButton = styled(Typography)`
  transition: color 0.2s ease-in-out;

  ${StyledButton}:hover & {
    color: ${({ theme, colorScheme }) =>
      colorScheme === "primary" ? theme.colors.buttonText :
      colorScheme === "textPrimary" ? theme.colors.primaryHover :
      colorScheme === "textDisabled" ? theme.colors.textDisabled :
      theme.colors.buttonText};
  }
    
`;
export const Button: React.FC<ButtonProps> = ({
  size = "medium",
  isLoading,
  children,
  className,
  icon,
  iconPosition = "left",
  onClick,
  disabled = false,
  iconOnly = false,
  outlined = false,
  rounded = false,
  ariaLabel,
  ...props
}) => {
  const handleClick = () => {
    if (disabled || isLoading) return;
    if (onClick) onClick();
  };

  return (
    <StyledButton
      size={size}
      className={className}
      ariaLabel={ariaLabel}
      onClick={handleClick}
      isLoading={isLoading}
      disabled={disabled}
      rounded={rounded}
      iconOnly={iconOnly}
      outlined={outlined}
      aria-busy={isLoading} // ✅ Improves Accessibility
      aria-label={iconOnly && children ? `${children} button` : undefined} // ✅ Accessibility
      {...props}
    >
      {isLoading && <span className="spinner" role="status"></span>}
      <span className="button-content">
  {iconOnly ? (
    icon
  ) : (
    <>
    <Flex flex="1 1 auto" align="center" gap="sm" justify="space-between" direction={"row"} fullWidth >
      {icon && iconPosition === "left" && <span>{icon}</span>}
      {children && <span>{children}</span>}
      {icon && iconPosition === "right" && <span>{icon}</span>}
      </Flex>
    </>
  )}
</span>
    </StyledButton>
  );
};